import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import { queryClient } from "@/lib/query-client";
import { useAuth } from "@/lib/auth-context";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  Projeto ativo
 *
 *  Todo o isolamento de dados assenta em `current_projeto_id()` no Postgres,
 *  que resolve, por ordem:
 *
 *    1. `profiles.projeto_ativo_id`  — validado contra `projeto_membros`
 *    2. cabeçalho `x-projeto-id`     — também validado contra a adesão
 *    3. adesão ativa mais antiga     — fallback
 *
 *  DESVIO DELIBERADO do padrão do nzmind-crm, que resolve pelo cabeçalho:
 *  o supabase-js não tem forma suportada de alterar cabeçalhos num cliente já
 *  criado. Guardar o projeto ativo na base torna a troca um simples UPDATE
 *  seguido de `queryClient.clear()`, em vez de recriar o cliente — o que
 *  derrubaria subscrições realtime e queries em curso.
 *
 *  A troca é apenas um pedido: a base valida a adesão. Escrever um
 *  `projeto_ativo_id` de um projeto onde o utilizador não é membro não dá
 *  acesso nenhum — `current_projeto_id()` ignora-o e cai no fallback.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface Projeto {
  id: string;
  codigo: string;
  nome: string;
  cliente: string | null;
  ativo: boolean;
  /**
   * A data a que TODOS os indicadores se referem.
   *
   * Vem aqui e não de uma query própria porque quase todos os ecrãs precisam de
   * a mostrar: um número medido a 30 de junho continua a ser esse número no dia
   * 5 de julho, e sem a data à vista parece que o painel está congelado.
   */
  status_date: string;
  /** Projeto pai, quando este é um subprojeto (PL-005, 15/09). */
  projeto_pai_id: string | null;
}

interface ProjetoContextType {
  projetoAtivo: Projeto | null;
  projetos: Projeto[];
  loading: boolean;
  trocarProjeto: (projetoId: string) => Promise<{ error: any }>;
  recarregarProjetos: () => Promise<void>;
}

const ProjetoContext = createContext<ProjetoContextType | undefined>(undefined);

export function ProjetoProvider({ children }: { children: React.ReactNode }) {
  const { user, profile, refreshProfile, loading: authLoading } = useAuth();
  // ⚠ O ID, e NUNCA o objeto `user`.
  //
  // O supabase entrega um `User` novo a cada evento de autenticação — e emite
  // um sempre que se volta ao separador. Com o objeto nas dependências,
  // `carregarProjetos` mudava de identidade, o efeito voltava a correr,
  // `setLoading(true)` disparava, e o `ProtectedRoute` desmontava a aplicação
  // inteira (28/08, slide 51). É a armadilha que `use-visao-colunas.ts`
  // documenta e evita de propósito, e em que este contexto tinha caído.
  const userId = user?.id;
  const [projetos, setProjetos] = useState<Projeto[]>([]);
  const [loading, setLoading] = useState(true);

  const carregarProjetos = useCallback(async () => {
    if (!userId) {
      setProjetos([]);
      setLoading(false);
      return;
    }
    // Voltar a levantar a bandeira em CADA execução que vá mesmo consultar.
    //
    // Sem isto: no ecrã de login não há sessão, esta função corre com `user`
    // nulo e deixa `loading = false` com `projetos = []`. Ao entrar, `user`
    // muda e a query recomeça — mas a bandeira continua em baixo e a lista
    // continua vazia. Nessa janela `projetoAtivo` é null e a guarda de rota
    // conclui "sem projeto", mandando para /sem-projeto um utilizador que tem
    // projeto. Como é um Navigate replace para fora da árvore protegida, ele
    // fica lá preso.
    setLoading(true);
    try {
      // A RLS já limita às adesões do utilizador — não é preciso filtrar aqui.
      const { data, error } = await supabase
        .from("projetos")
        .select("id, codigo, nome, cliente, ativo, status_date, projeto_pai_id")
        .eq("ativo", true)
        .order("nome");
      if (error) throw error;
      setProjetos((data as Projeto[]) ?? []);
    } catch (err) {
      console.error("Falha ao carregar projetos:", err);
      setProjetos([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (authLoading) return;
    void carregarProjetos();
  }, [authLoading, carregarProjetos]);

  const trocarProjeto = useCallback(
    async (projetoId: string) => {
      if (!userId) return { error: new Error("Sem sessão") };
      const { error } = await supabase
        .from("profiles")
        .update({ projeto_ativo_id: projetoId })
        .eq("user_id", userId);
      if (error) return { error };

      // A cache inteira é do projeto anterior. Limpar tudo é mais barato e
      // muito mais seguro do que tentar invalidar seletivamente 13 módulos.
      queryClient.clear();
      await refreshProfile();
      return { error: null };
    },
    [userId, refreshProfile],
  );

  const projetoAtivo =
    projetos.find((p) => p.id === profile?.projeto_ativo_id) ?? projetos[0] ?? null;

  // Um literal de objeto aqui invalida TODOS os consumidores a cada render do
  // provider — e há um render por evento de autenticação.
  const valor = useMemo(
    () => ({
      projetoAtivo,
      projetos,
      loading,
      trocarProjeto,
      recarregarProjetos: carregarProjetos,
    }),
    [projetoAtivo, projetos, loading, trocarProjeto, carregarProjetos],
  );

  return <ProjetoContext.Provider value={valor}>{children}</ProjetoContext.Provider>;
}

export function useProjeto() {
  const ctx = useContext(ProjetoContext);
  if (!ctx) throw new Error("useProjeto tem de ser usado dentro de ProjetoProvider");
  return ctx;
}
